/**
 * One app config from the server (docs/redesign-2026-11/resilience.md §2–§4), hand-written next to the generated
 * platform types while the platform ships it:
 *
 * - `GET /v1/app/config` (public, no auth, `cache-control: public, max-age=60`, ETag): features, rules, minimum
 *   client versions and the notices active now.
 * - Staff (contracts/platform-api.md "App config, notices and minimum versions"): `GET/POST /v1/admin/notices`,
 *   `PATCH /v1/admin/notices/{id}`, `DELETE /v1/admin/notices/{id}` (body `{reason}`), `GET/PUT
 *   /v1/admin/app-min-version` (`{min_version, updated_at, updated_by, history}`); every write takes a reason.
 * - Rules that come with objects (realdata): `annotation_rules` on the recording spec and capture, score `lines` and
 *   `total_label`; week statements' `items_by_kind[].label`.
 *
 * Clients read everything through `normalizeAppConfig` / `normalizeAnnotationRules`, which fill whatever is missing or
 * malformed with today's values, so a failed or older server changes nothing.
 */
import type { ApiClient } from './client.ts'

const enc = encodeURIComponent

// ── The config ───────────────────────────────────────────────────────────────────────────────────────────────

export type NoticeTone = 'info' | 'warn' | 'critical'
export type NoticeSurface = 'web' | 'phone'

export interface AppNotice {
  id: string
  tone: NoticeTone
  title: string
  body: string
  /** An in-app path ("/wallet") or an https link; null for none. */
  link: string | null
  surfaces: NoticeSurface[]
  starts_at: string | null
  ends_at: string | null
  /** Critical notices are never dismissible, whatever this says. */
  dismissible: boolean
}

export interface AppMinVersion {
  /** `build` = Android versionCode / iOS build number; `js` = the bundle's integer `APP_JS_VERSION`. */
  android: { build: number | null; js: number | null }
  ios: { build: number | null; js: number | null }
  /** `js` = the web bundle's `WEB_JS_VERSION`. */
  web: { js: number | null }
  message: string | null
}

export interface AppFeatures {
  /** The control modes that are on; null = the server didn't say (read `/v1/control-modes`). */
  control_modes: string[] | null
  /** The settlement adapter; null = the server didn't say. */
  settlement: 'dry-run' | 'live' | (string & {}) | null
  /** Path statuses by key (open, pilot, coming); hidden paths are left out (= not offered); null = the server didn't
   * say. */
  paths: Record<string, string> | null
  /** Cash out (selling paid alpha for TAO, signed in the person's own wallet) is offered; false when the server
   * didn't say. */
  cash_out?: boolean
  /** WalletConnect project id for linking and signing with a phone wallet app (QR on the web); null = not offered. */
  walletconnect_project_id?: string | null
}

export interface AppRules {
  calls: { ring_s: number }
  payout_min_rao: number
  /** Withdraw shortcuts in basis points of the withdrawable amount (10000 = Max). */
  withdraw_shortcuts_bps: number[]
}

export interface AppConfig {
  v: number
  updated_at: string | null
  features: AppFeatures
  rules: AppRules
  min_version: AppMinVersion
  notices: AppNotice[]
}

/** Today's values: what a client uses until (or unless) the server says otherwise. */
export const DEFAULT_APP_CONFIG: AppConfig = Object.freeze({
  v: 1,
  updated_at: null,
  features: { control_modes: null, settlement: null, paths: null },
  rules: { calls: { ring_s: 60 }, payout_min_rao: 1_000_000_000, withdraw_shortcuts_bps: [2500, 5000, 10000] },
  min_version: { android: { build: null, js: null }, ios: { build: null, js: null }, web: { js: null }, message: null },
  notices: [],
}) as AppConfig

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)
const posInt = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) && x >= 0 ? Math.floor(x) : null)
const str = (x: unknown): string | null => (typeof x === 'string' && x.trim() ? x : null)
const strList = (x: unknown): string[] | null => (Array.isArray(x) ? x.filter((v): v is string => typeof v === 'string') : null)

const TONES: readonly string[] = ['info', 'warn', 'critical']

/** One notice from the server, or null when it can't be shown (no id or no title). An unknown tone reads as info. */
export function normalizeNotice(x: unknown): AppNotice | null {
  if (!isObj(x)) return null
  const id = str(x.id)
  const title = str(x.title)
  if (!id || !title) return null
  const tone = (typeof x.tone === 'string' && TONES.includes(x.tone) ? x.tone : 'info') as NoticeTone
  const surfaces = (strList(x.surfaces) ?? ['web', 'phone']) as NoticeSurface[]
  return {
    id,
    tone,
    title,
    body: typeof x.body === 'string' ? x.body : '',
    link: str(x.link),
    surfaces,
    starts_at: str(x.starts_at),
    ends_at: str(x.ends_at),
    dismissible: tone === 'critical' ? false : x.dismissible !== false,
  }
}

function normalizeMinVersion(x: unknown): AppMinVersion {
  const d = DEFAULT_APP_CONFIG.min_version
  if (!isObj(x)) return { android: { ...d.android }, ios: { ...d.ios }, web: { ...d.web }, message: null }
  const pair = (y: unknown) => (isObj(y) ? { build: posInt(y.build), js: posInt(y.js) } : { build: null, js: null })
  return { android: pair(x.android), ios: pair(x.ios), web: { js: isObj(x.web) ? posInt(x.web.js) : null }, message: str(x.message) }
}

/** The server's config with today's values wherever it is missing or malformed. Never throws. */
export function normalizeAppConfig(raw: unknown): AppConfig {
  const d = DEFAULT_APP_CONFIG
  if (!isObj(raw)) return { ...d, features: { ...d.features }, rules: { ...d.rules, calls: { ...d.rules.calls } }, min_version: normalizeMinVersion(null), notices: [] }
  const f = isObj(raw.features) ? raw.features : {}
  const r = isObj(raw.rules) ? raw.rules : {}
  const calls = isObj(r.calls) ? r.calls : {}
  const shortcuts = Array.isArray(r.withdraw_shortcuts_bps) ? r.withdraw_shortcuts_bps.map(posInt).filter((n): n is number => n !== null && n > 0 && n <= 10000) : []
  const paths = isObj(f.paths) ? Object.fromEntries(Object.entries(f.paths).filter((e): e is [string, string] => typeof e[1] === 'string')) : null
  return {
    v: posInt(raw.v) ?? 1,
    updated_at: str(raw.updated_at),
    features: { control_modes: strList(f.control_modes), settlement: str(f.settlement), paths, cash_out: f.cash_out === true, walletconnect_project_id: str(f.walletconnect_project_id) },
    rules: {
      calls: { ring_s: posInt(calls.ring_s) || d.rules.calls.ring_s },
      payout_min_rao: posInt(r.payout_min_rao) || d.rules.payout_min_rao,
      withdraw_shortcuts_bps: shortcuts.length ? shortcuts : [...d.rules.withdraw_shortcuts_bps],
    },
    min_version: normalizeMinVersion(raw.min_version),
    notices: Array.isArray(raw.notices) ? raw.notices.map(normalizeNotice).filter((n): n is AppNotice => n !== null) : [],
  }
}

/** The notices to show on `surface` now: started (or no start), not ended, for that surface; critical ones first. */
export function activeNotices(cfg: Pick<AppConfig, 'notices'>, surface: NoticeSurface, now = Date.now()): AppNotice[] {
  const rank: Record<NoticeTone, number> = { critical: 0, warn: 1, info: 2 }
  return cfg.notices
    .filter((n) => n.surfaces.includes(surface))
    .filter((n) => !n.starts_at || !(Date.parse(n.starts_at) > now))
    .filter((n) => !n.ends_at || !(Date.parse(n.ends_at) <= now))
    .sort((a, b) => rank[a.tone] - rank[b.tone])
}

/** True when the server asks for a newer web bundle than `webJs` (the bundle's `WEB_JS_VERSION`). */
export function webOutdated(cfg: Pick<AppConfig, 'min_version'>, webJs: number): boolean {
  const min = cfg.min_version.web.js
  return typeof min === 'number' && webJs < min
}

/** The withdraw shortcuts as labels and fractions: [2500, 5000, 10000] → 25%, 50%, Max. */
export function withdrawShortcuts(bps: readonly number[]): { bps: number; label: string }[] {
  return bps.map((b) => ({ bps: b, label: b >= 10000 ? 'Max' : `${+(b / 100).toFixed(2)}%` }))
}

// ── Rules that come with objects (realdata) ─────────────────────────────────────────────────────────────────

export type AnnotationRoleKey = 'environment' | 'activity' | 'subtask' | 'action' | 'object' | (string & {})
export type ProofRule = 'frame' | 'clip' | 'frame_or_clip'

export interface AnnotationRoleRule {
  role: AnnotationRoleKey
  required: boolean
  proof: ProofRule
  max: number | null
}

/** The recording's labelling rules as the server serves them (`annotation_rules`). Editors read them to draw (a clip's
 * length, which proof a row shows); the server alone decides, and says what's wrong as `problems`. */
export interface AnnotationRules {
  roles: AnnotationRoleRule[]
  /** The least video a recording keeps (10 minutes on servers from November 2026); 0 when not served. */
  recording_min_ms: number
  clip_min_ms: number
  clip_max_ms: number
  part_min_ms: number
  retained_min_ms: number
  max_parts: number
  max_labels_per_part: number
  max_bookmarks: number
  max_proposals: number
}

/** What an editor draws with when a server serves no rules (older servers); never a reason to refuse anything. */
export const DEFAULT_ANNOTATION_RULES: AnnotationRules = Object.freeze({
  roles: [
    { role: 'environment', required: true, proof: 'frame', max: 1 },
    { role: 'activity', required: true, proof: 'clip', max: 1 },
    { role: 'subtask', required: false, proof: 'clip', max: null },
    { role: 'action', required: false, proof: 'clip', max: null },
    { role: 'object', required: false, proof: 'frame_or_clip', max: null },
  ],
  recording_min_ms: 0,
  clip_min_ms: 1000,
  clip_max_ms: 10_000,
  part_min_ms: 5000,
  retained_min_ms: 10_000,
  max_parts: 50,
  max_labels_per_part: 60,
  max_bookmarks: 200,
  max_proposals: 20,
}) as AnnotationRules

const PROOFS: readonly string[] = ['frame', 'clip', 'frame_or_clip']

/** The server's `annotation_rules` with today's values wherever missing or malformed; roles the server leaves out keep
 * today's rule. A served value always wins, 0 included. Never throws. */
export function normalizeAnnotationRules(raw: unknown): AnnotationRules {
  const d = DEFAULT_ANNOTATION_RULES
  if (!isObj(raw)) return { ...d, roles: d.roles.map((r) => ({ ...r })) }
  const num = (k: keyof Omit<AnnotationRules, 'roles'>) => posInt(raw[k]) ?? d[k]
  const roles = new Map(d.roles.map((r) => [r.role, { ...r }]))
  if (Array.isArray(raw.roles))
    for (const x of raw.roles) {
      if (!isObj(x) || typeof x.role !== 'string') continue
      const base = roles.get(x.role) ?? { role: x.role, required: false, proof: 'frame_or_clip' as ProofRule, max: null }
      roles.set(x.role, {
        role: x.role,
        required: typeof x.required === 'boolean' ? x.required : base.required,
        proof: typeof x.proof === 'string' && PROOFS.includes(x.proof) ? (x.proof as ProofRule) : base.proof,
        max: x.max === null ? null : (posInt(x.max) ?? base.max),
      })
    }
  const out: AnnotationRules = {
    roles: [...roles.values()],
    recording_min_ms: num('recording_min_ms'),
    clip_min_ms: num('clip_min_ms'),
    clip_max_ms: num('clip_max_ms'),
    part_min_ms: num('part_min_ms'),
    retained_min_ms: num('retained_min_ms'),
    max_parts: num('max_parts'),
    max_labels_per_part: num('max_labels_per_part'),
    max_bookmarks: num('max_bookmarks'),
    max_proposals: num('max_proposals'),
  }
  if (out.clip_max_ms < out.clip_min_ms) out.clip_max_ms = out.clip_min_ms
  return out
}

/** The proof a role needs under `rules` (an unknown role: a frame or a clip). */
export function proofRuleOf(rules: Pick<AnnotationRules, 'roles'>, role: string): ProofRule {
  return rules.roles.find((r) => r.role === role)?.proof ?? 'frame_or_clip'
}

/** Does a bookmark of `dur_ms` (0 = a frame) prove a label of `role` under `rules`? */
export function provesUnder(rules: AnnotationRules, role: string, durMs: number): boolean {
  const clip = durMs >= rules.clip_min_ms && durMs <= rules.clip_max_ms
  const p = proofRuleOf(rules, role)
  if (p === 'frame') return durMs === 0
  if (p === 'clip') return clip
  return durMs === 0 || clip
}

/** "1–10 s" for the clip window. */
export function clipRangeText(rules: Pick<AnnotationRules, 'clip_min_ms' | 'clip_max_ms'>): string {
  return `${secondsText(rules.clip_min_ms)}–${secondsText(rules.clip_max_ms)} s`
}

/** 5000 → "5", 2500 → "2.5". */
export function secondsText(ms: number): string {
  return String(+(ms / 1000).toFixed(1))
}

/** A server-written score line (`GET /v1/me/captures/{id}` → `score.lines`). */
export interface ScoreLine {
  key: string
  label: string
  value: number
}

/** The server's score lines when present and well-formed, else null (the client keeps today's layout). */
export function scoreLines(score: { lines?: unknown } | null | undefined): ScoreLine[] | null {
  const raw = score?.lines
  if (!Array.isArray(raw)) return null
  const out = raw.filter((l): l is ScoreLine => isObj(l) && typeof l.label === 'string' && typeof l.value === 'number' && Number.isFinite(l.value)).map((l) => ({ key: String(l.key ?? l.label), label: l.label, value: l.value }))
  return out.length ? out : null
}

// ── Staff (ops) ──────────────────────────────────────────────────────────────────────────────────────────────

/** A notice as staff see it: every notice, whatever its window, with who changed it last. */
export interface AdminNotice extends AppNotice {
  /** The server's view of its window; worked out from the times when missing. */
  status: NoticeState
  created_at?: string | null
  updated_at?: string | null
  created_by?: { id: string; name: string | null } | null
  updated_by?: { id: string; name: string | null } | null
}

export interface NoticeInput {
  tone: NoticeTone
  title: string
  body: string
  link?: string | null
  surfaces: NoticeSurface[]
  starts_at?: string | null
  ends_at?: string | null
  dismissible?: boolean
  /** Audited. */
  reason: string
}

export type NoticePatch = Partial<Omit<NoticeInput, 'reason'>> & { reason: string }

export interface MinVersionChange {
  at: string
  by: { id: string; name: string | null } | null
  before: AppMinVersion | null
  after: AppMinVersion | null
  reason: string
}

/** `GET /v1/admin/app-min-version` (and the answer to PUT). */
export interface AdminAppMinVersion {
  min_version: AppMinVersion
  updated_at: string | null
  updated_by: { id: string; name: string | null } | null
  history: MinVersionChange[]
}

/** PUT replaces the whole setting: a field left out (or null) = no minimum. */
export type AppMinVersionBody = AppMinVersion & { reason: string }

export type NoticeState = 'scheduled' | 'active' | 'ended'

/** Where a notice is in its window now. */
export function noticeState(n: Pick<AppNotice, 'starts_at' | 'ends_at'>, now = Date.now()): NoticeState {
  if (n.ends_at && Date.parse(n.ends_at) <= now) return 'ended'
  if (n.starts_at && Date.parse(n.starts_at) > now) return 'scheduled'
  return 'active'
}

function noticeList(r: unknown): AdminNotice[] {
  const list = Array.isArray(r) ? r : isObj(r) ? (Array.isArray(r.items) ? r.items : Array.isArray(r.notices) ? r.notices : []) : []
  return list.flatMap((x) => {
    const n = normalizeNotice(x)
    if (!n || !isObj(x)) return []
    const status = typeof x.status === 'string' && ['scheduled', 'active', 'ended'].includes(x.status) ? (x.status as NoticeState) : noticeState(n)
    return [{ ...n, status, created_at: str(x.created_at), updated_at: str(x.updated_at), created_by: (x.created_by as AdminNotice['created_by']) ?? null, updated_by: (x.updated_by as AdminNotice['updated_by']) ?? null }]
  })
}

function minVersionView(r: unknown): AdminAppMinVersion {
  const x = isObj(r) ? r : {}
  // the view nests the setting under `min_version`; tolerate a bare setting too
  const mv = isObj(x.min_version) ? x.min_version : x
  return {
    min_version: normalizeMinVersion(mv),
    updated_at: str(x.updated_at),
    updated_by: (x.updated_by as AdminAppMinVersion['updated_by']) ?? null,
    history: Array.isArray(x.history) ? (x.history.filter(isObj) as unknown as MinVersionChange[]) : [],
  }
}

/** The PUT body: whole numbers ≥ 1 or nothing (no minimum), the message trimmed. */
export function minVersionBody(v: AppMinVersion, reason: string): AppMinVersionBody {
  const n = (x: number | null) => (typeof x === 'number' && Number.isFinite(x) && x >= 1 ? Math.floor(x) : null)
  return {
    android: { build: n(v.android.build), js: n(v.android.js) },
    ios: { build: n(v.ios.build), js: n(v.ios.js) },
    web: { js: n(v.web.js) },
    message: v.message?.trim() || null,
    reason,
  }
}

export function appConfigApi(c: ApiClient) {
  return {
    /** Public (the phone reads it before sign-in); normalized, so callers always get a complete config. */
    config: async () => normalizeAppConfig(await c.get<unknown>('platform', '/v1/app/config', { auth: false })),
    admin: {
      notices: async () => noticeList(await c.get<unknown>('platform', '/v1/admin/notices')),
      createNotice: (body: NoticeInput) => c.post<AdminNotice>('platform', '/v1/admin/notices', body),
      updateNotice: (id: string, body: NoticePatch) => c.patch<AdminNotice>('platform', `/v1/admin/notices/${enc(id)}`, body),
      /** Ends it now (keeps it in the list as ended). */
      endNotice: (id: string, reason: string, now = new Date()) => c.patch<AdminNotice>('platform', `/v1/admin/notices/${enc(id)}`, { ends_at: now.toISOString(), reason }),
      /** The row is kept on the server but leaves every list. */
      deleteNotice: (id: string, reason: string) => c.delete<{ id: string; deleted: true }>('platform', `/v1/admin/notices/${enc(id)}`, { body: { reason } }),
      minVersion: async (): Promise<AdminAppMinVersion> => minVersionView(await c.get<unknown>('platform', '/v1/admin/app-min-version')),
      /** Replaces the whole setting. */
      setMinVersion: async (body: AppMinVersionBody): Promise<AdminAppMinVersion> => minVersionView(await c.put<unknown>('platform', '/v1/admin/app-min-version', body)),
    },
  }
}

export type AppConfigApi = ReturnType<typeof appConfigApi>
